import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import EventIcon from "@mui/icons-material/Event";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { makeQueryString } from "../APIs/request";
import { usePaginationParams } from "../hooks/usePaginationParams";
import { useMe } from "../hooks/useMe";
import Sidebar from "../components/Sidebar";
import API from "../APIs/API";
import "../styles/list.scss";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { ADMIN_LOCATION_LEVEL_USER_OPTIONS } from "../constants";

// scope: 'main' | 'non_teachers' | 'teachers' | 'agency'
const Events = ({ scope, type }) => {
  const { admin_location_id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { permissions, user: loggedUser } = useMe();
  const { gridParams, queryParams } = usePaginationParams();

  const [showAddModal, setShowAddModal] = useState(false);
  const [form, setForm] = useState({ name: "", fromDate: null, toDate: null, recurring: false });

  const pageUserType = type === "Teacher" ? "teachers" : "non-teachers";

  // Determine back URL
  const backUrl =
    scope === "main" ? "/dashboard" :
    scope === "non_teachers" ? "/non-teachers" :
    scope === "teachers" ? "/teachers" :
    `/${pageUserType}`;

  // Determine page title
  const { data: adminLocation } = useQuery(
    ["admin_location", admin_location_id],
    { queryFn: async () => { const r = await new API().getAdminLocationById(admin_location_id); return r?.data ?? null; }, enabled: scope === "agency" }
  );

  const pageTitle =
    scope === "main" ? "Main Events" :
    scope === "non_teachers" ? "Non-Teachers Events" :
    scope === "teachers" ? "Teachers Events" :
    `Events — ${adminLocation?.admin_desc ?? ""}`;

  // Who can add/edit at this scope?
  const canWrite = (() => {
    if (scope === "main") return permissions.superRead;
    if (scope === "non_teachers") return permissions.superRead || permissions.non_teacher;
    if (scope === "teachers") return permissions.superRead || permissions.teacher;
    if (scope === "agency") {
      if (permissions.superRead) return true;
      if (permissions.topLevel) return true;
      if (loggedUser?.admin_location_level_user === ADMIN_LOCATION_LEVEL_USER_OPTIONS.ADMIN_LOCATION_USER) return true;
      return false;
    }
    return false;
  })();

  // Fetch events
  // main scope → getEvents?scope=main (admin management view, no cascade)
  // non_teachers/teachers/agency → getEffectiveEvents (cascade: main + sector [+ agency])
  const effectiveScopeUserType =
    scope === "non_teachers" ? "Non-Teacher" :
    scope === "teachers" ? "Teacher" :
    type ?? "Non-Teacher";

  const queryKey = scope === "main"
    ? ["events", scope, makeQueryString(queryParams)]
    : ["events_effective", scope, admin_location_id ?? "none", makeQueryString(queryParams)];

  const { data: eventsData = {}, isFetching } = useQuery(queryKey, {
    queryFn: async () => {
      if (scope === "main") {
        const qs = makeQueryString(queryParams, { scope });
        const res = await new API().getEvents(qs);
        if (!res) { toast.error("Failed to load events."); return {}; }
        return res;
      }
      const params = { user_type: effectiveScopeUserType, ...queryParams };
      if (scope === "agency") params.admin_location_id = admin_location_id;
      const qs = makeQueryString(params);
      const res = await new API().getEffectiveEvents(qs);
      if (!res) { toast.error("Failed to load events."); return {}; }
      return res;
    },
    keepPreviousData: true,
    retry: false,
  });

  // Create mutation
  const createMutation = useMutation(
    (data) => new API().createEvent(data),
    {
      onSuccess: (res) => {
        if (res?.status === "ok") {
          toast.success("Event added!");
          queryClient.invalidateQueries(["events"]);
          queryClient.invalidateQueries(["events_effective"]);
          setShowAddModal(false);
          setForm({ name: "", fromDate: null, toDate: null, recurring: false });
        } else {
          toast.error("Failed to add event.");
        }
      },
      onError: () => toast.error("Failed to add event."),
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.warning("Event name is required."); return; }
    if (!form.fromDate) { toast.warning("From date is required."); return; }
    if (!form.toDate) { toast.warning("To date is required."); return; }
    if (dayjs(form.toDate).isBefore(dayjs(form.fromDate))) {
      toast.warning("To date must be on or after From date.");
      return;
    }
    createMutation.mutate({
      name: form.name.trim(),
      from_date: dayjs(form.fromDate).format("YYYY-MM-DD"),
      to_date: dayjs(form.toDate).format("YYYY-MM-DD"),
      recurring: form.recurring,
      scope,
      admin_location_id: scope === "agency" ? Number(admin_location_id) : null,
    });
  };

  const formatDate = (d) => d ? dayjs(d).format("DD/MM/YYYY") : "";
  const formatDateRange = (from, to) =>
    from === to ? formatDate(from) : `${formatDate(from)} – ${formatDate(to)}`;

  const scopeLabel = (s) => ({
    main: "Main",
    non_teachers: "Non-Teachers",
    teachers: "Teachers",
    agency: "Agency",
  })[s] ?? s;

  const actionColumn = {
    field: "action",
    headerName: "Action",
    width: 100,
    sortable: false,
    renderCell: ({ row }) => (
      <div className="cellAction">
        <div
          className="viewButton"
          onClick={() => {
            const base = scope === "main" ? "/main-events" :
              scope === "non_teachers" ? "/non-teachers-events" :
              scope === "teachers" ? "/teachers-events" :
              `/${pageUserType}/${admin_location_id}/events`;
            navigate(`${base}/${row.id}`);
          }}
        >
          View
        </div>
      </div>
    ),
  };

  const columns = [
    actionColumn,
    {
      field: "date_range",
      headerName: "Date",
      width: 220,
      sortable: false,
      renderCell: ({ row }) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span>{formatDateRange(row.from_date, row.to_date)}</span>
          {row.recurring && (
            <span style={{ fontSize: 11, color: "#7c3aed", fontWeight: 600 }}>↻ Recurring</span>
          )}
        </div>
      ),
    },
    { field: "name", headerName: "Event", width: 280 },
    ...(scope === "agency" ? [{
      field: "scope",
      headerName: "Source",
      width: 140,
      sortable: false,
      renderCell: ({ row }) => (
        <span style={{
          fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10,
          backgroundColor: row.scope === "agency" ? "#dbeafe" : "#f3e8ff",
          color: row.scope === "agency" ? "#1d4ed8" : "#6d28d9",
        }}>
          {scopeLabel(row.scope)}
        </span>
      ),
    }] : []),
    {
      field: "created_at",
      headerName: "Created at",
      width: 180,
      renderCell: ({ row }) => row.created_at ? dayjs(row.created_at).format("DD/MM/YYYY HH:mm") : "",
    },
    {
      field: "updated_at",
      headerName: "Updated at",
      width: 180,
      renderCell: ({ row }) => row.updated_at ? dayjs(row.updated_at).format("DD/MM/YYYY HH:mm") : "",
    },
  ];

  return (
    <div className="wrapper">
      <div className="sidebar"><Sidebar /></div>
      <div className="content">
        <div className="list">
          <div className="listContainer">
            <div className="datatable">
              <Link to={backUrl} className="backButtonSection">
                <KeyboardBackspaceIcon className="backButton" />
              </Link>
              <br /><br />

              <div className="datatableTitle">
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <EventIcon style={{ color: "#7c3aed" }} />
                  {pageTitle}
                </span>
                {canWrite && (
                  <button className="addBtn" onClick={() => setShowAddModal(true)}>
                    + Add Event
                  </button>
                )}
              </div>
              <br />

              <DataGrid
                className="datagrid"
                rows={eventsData?.rows ?? []}
                columns={columns}
                rowCount={eventsData?.count ?? 0}
                loading={isFetching}
                getRowHeight={() => "auto"}
                sx={{
                  "& .MuiDataGrid-cell": { paddingTop: "10px", paddingBottom: "10px" },
                }}
                {...gridParams}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Add Event Modal ── */}
      {showAddModal && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1300, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setShowAddModal(false)}
        >
          <div
            style={{ background: "#fff", borderRadius: 10, padding: "32px 36px", minWidth: 440, maxWidth: 520, boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: 20, color: "#1e1e2e" }}>Add Event</h3>
            <form onSubmit={handleSubmit}>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 6, fontWeight: 600, fontSize: 14 }}>Event Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Christmas Day"
                  style={{ width: "100%", padding: "9px 12px", fontSize: 14, border: "1.5px solid #d1d5db", borderRadius: 7, boxSizing: "border-box", outline: "none" }}
                />
              </div>

              <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", marginBottom: 6, fontWeight: 600, fontSize: 14 }}>From Date *</label>
                  <DatePicker
                    value={form.fromDate}
                    onChange={(v) => setForm({ ...form, fromDate: v, toDate: form.toDate ?? v })}
                    renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                    inputFormat="DD/MM/YYYY"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", marginBottom: 6, fontWeight: 600, fontSize: 14 }}>To Date *</label>
                  <DatePicker
                    value={form.toDate}
                    onChange={(v) => setForm({ ...form, toDate: v })}
                    renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                    inputFormat="DD/MM/YYYY"
                    minDate={form.fromDate}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.recurring}
                      onChange={(e) => setForm({ ...form, recurring: e.target.checked })}
                      color="secondary"
                    />
                  }
                  label={<span style={{ fontSize: 14, fontWeight: 600 }}>Recurring annually</span>}
                />
                <p style={{ fontSize: 12, color: "#6b7280", marginTop: 2, marginLeft: 2 }}>
                  {form.recurring ? "This event repeats every year on the same date(s)." : "This event occurs once only."}
                </p>
              </div>

              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button type="button" className="app-button app-button-gray" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className="app-button app-button-primary" disabled={createMutation.isLoading}>
                  {createMutation.isLoading ? "Saving…" : "Save Event"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;
